import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AnnotationCommand } from './pdf-commands/annotation.command';
import { PdfAnnotationActions } from 'src/app/core/pdf-utils/enums';
import { TextAnnotationCommand } from './pdf-commands/text-annotation.command';
import { DocumentManagerService } from '../document/document-manager.service';
import { AutosaveManagerService } from '../autosave/autosave-manager.service';
import { ICommand } from './commands.interface';

interface DocumentCommandState {
  commandsList: ICommand[] | null;
  currentCommandIndex: number;
  skipNextCommandAction: PdfAnnotationActions[] | null;
  pendingCommand: TextAnnotationCommand | null;
}

@Injectable({
  providedIn: 'root'
})
export class CommandInvokerService {
  private commandsListMap: Map<string, DocumentCommandState> = new Map();
  private currentDocId: string | null = null;

  private get currentCommandIndex() {
    if (this.docStore) {
      return this.docStore.currentCommandIndex;
    }
    return -1;
  }

  private set currentCommandIndex(e: number) {
    if (this.docStore) {
      this.docStore.currentCommandIndex = e;
    }
  }

  private get skipNextCommandAction() {
    if (this.docStore) {
      return this.docStore.skipNextCommandAction;
    }
    return null;
  }

  private set skipNextCommandAction(e: PdfAnnotationActions[] | null) {
    if (this.docStore) {
      this.docStore.skipNextCommandAction = e;
    }
  }

  private currentCommandIndexSubject: BehaviorSubject<number> = new BehaviorSubject(-1);
  private commandsListLengthSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private get docId() {
    if (!this.currentDocId) {
      return null;
    }
    return this.currentDocId;
  }

  private set docId(id: string | null) {
    this.currentDocId = id;
  }

  private get docStore(): DocumentCommandState | null {
    if (this.docId) {
      const store = this.commandsListMap.get(this.docId);
      if (!store && this.docId) {
        return this.setDocStore(this.docId);
      }
      if (store) {
        return store;
      }
    }
    return null;
  }

  private setDocStore(docId: string) {
    const newStore = {
      commandsList: [],
      currentCommandIndex: -1,
      skipNextCommandAction: null,
      pendingCommand: null
    };
    this.commandsListMap.set(docId, newStore);
    return newStore;
  }

  private get commandsList() {
    if (this.docStore) {
      return this.docStore.commandsList;
    }
    return null;
  }

  private set commandsList(e: ICommand[] | null) {
    if (this.docStore) {
      this.docStore.commandsList = e;
    }
  }

  private get pendingCommand() {
    if (this.docStore) {
      return this.docStore.pendingCommand;
    }
    return null;
  }

  private set pendingCommand(e: TextAnnotationCommand | null) {
    if (this.docStore) {
      this.docStore.pendingCommand = e;
    }
  }

  public get getCurrentUndoRedoIndexSubject() {
    return this.currentCommandIndexSubject.asObservable();
  }

  public get commandsListLengthSubj() {
    return this.commandsListLengthSubject.asObservable();
  }

  private isCurrentCommandisAnnotationCommand(redo: boolean = false): PdfAnnotationActions | null {
    if (this.commandsList && this.commandsList.length > 0) {
      const index = redo ? this.currentCommandIndex + 1 : this.currentCommandIndex;
      if (this.commandsList[index] && this.commandsList[index] instanceof AnnotationCommand) {
        return (this.commandsList[index] as AnnotationCommand).action;
      } else if (this.commandsList[index] && this.commandsList[index] instanceof TextAnnotationCommand) {
        return PdfAnnotationActions.Modified;
      }
    }
    return null;
  }

  public get nextSkippedCommand(): PdfAnnotationActions | null {
    return this.skipNextCommandAction && this.skipNextCommandAction.length ? this.skipNextCommandAction[0] : null;
  }

  constructor(
    private readonly docMgrService: DocumentManagerService,
    private readonly ngZone: NgZone,
    private readonly autosave: AutosaveManagerService,
  ) {
    this.initSbuscriptions();
  }

  private initSbuscriptions() {
    this.docMgrService.currentDocumentChanged.subscribe(e => {
      if (!this.docMgrService.isFocusDocument) {
        this.docId = e;
        if (this.docId && !this.docStore) {
          this.setDocStore(this.docId);
        }
        this.updateSubjects();
      }
    });
  }

  async ExecuteCommand(command: ICommand): Promise<any> {
    if (this.docMgrService.isFocusDocument || this.docMgrService.currentDoc && !this.docMgrService.currentDoc.isDocumentReady) {
      return;
    }
    if (this.nextSkippedCommand && command instanceof AnnotationCommand && command.action === this.nextSkippedCommand) {
      if (this.skipNextCommandAction && this.skipNextCommandAction.length === 2) {
        this.skipNextCommandAction.splice(0, 1);
        return;
      } else {
        this.skipNextCommandAction = null;
        if (this.pendingCommand) {
          this.pendingCommand.nextValueRecieved = command.annots;
        } else {
          return;
        }
      }
    } else if (command instanceof TextAnnotationCommand) {
      this.pendingCommand = command;
      this.skipNextCommandAction = [PdfAnnotationActions.Deleted, PdfAnnotationActions.Added];
      return;
    }

    if (this.commandsList && this.currentCommandIndex !== this.commandsList.length - 1) {
      this.commandsList.length = this.currentCommandIndex + 1;
    }

    if (this.pendingCommand && this.docStore && this.docStore.commandsList) {
      this.docStore.commandsList.push(this.pendingCommand);
    } else {
      if (this.docStore && this.docStore.commandsList) {
        this.docStore.commandsList.push(command);
      }
    }

    this.skipNextCommandAction = null;
    this.pendingCommand = null;
    if (this.commandsList) {
      this.currentCommandIndex = this.commandsList.length - 1;
    }

    /* trigger auto save here */
    await this.autosave.triggerAutoSave();

    this.updateSubjects();
  }

  public async undo() {
    // for AnnotationCommand pdf only 
    this.skipNextCommandAction = this.findNextSkipCommand(false);

    let indx = this.currentCommandIndex;

    if (this.commandsList !== null) {
      await this.commandsList[indx--].undo();
    }
    this.currentCommandIndex = indx;
    this.updateSubjects();
  }

  public async redo() {
    // for AnnotationCommand pdf only 
    this.skipNextCommandAction = this.findNextSkipCommand(true);

    let indx = this.currentCommandIndex;
    if (this.commandsList !== null) {
      await this.commandsList[++indx].execute();
    }
    this.currentCommandIndex = indx;
    this.updateSubjects();
  }

  clear() {
    this.skipNextCommandAction = null;
    this.commandsList = [];
    this.currentCommandIndex = this.commandsList.length - 1;
    this.updateSubjects();
  }

  private updateSubjects() {
    this.ngZone.run(() => {
      if (!this.docId) {
        this.currentCommandIndexSubject.next(-1);
        this.commandsListLengthSubject.next(0);
      } else {
        this.currentCommandIndexSubject.next(this.currentCommandIndex);
        if (this.commandsList) {
          this.commandsListLengthSubject.next(this.commandsList.length);
        }
      }
    });
  }

  private findNextSkipCommand(redo: boolean): PdfAnnotationActions[] | null {
    const action = this.isCurrentCommandisAnnotationCommand(redo);
    if (action) {
      switch (action) {
        // example: command added
        // so when undo we need to skip delete command
        // but when redo we need to skip add command
        case PdfAnnotationActions.Added:
          return (redo ? [PdfAnnotationActions.Added] : [PdfAnnotationActions.Deleted]);
        case PdfAnnotationActions.Deleted:
          return (redo ? [PdfAnnotationActions.Deleted] : [PdfAnnotationActions.Added]);
        case PdfAnnotationActions.Modified:
          return [PdfAnnotationActions.Deleted, PdfAnnotationActions.Added];
      }
    }
    return null;
  }

}

